import {getRequestState, getRequestStateSuccess, RequestState} from "../../models/common/RequestState";
import {
    FETCH_INITIAL_SELECT_OPTIONS,
    FETCH_SELECT_OPTIONS,
    FETCH_SELECT_OPTIONS_ERROR,
    FETCH_SELECT_OPTIONS_SUCCESS,
    FetchSelectOptionTypes
} from "../../actions/common/SelectActions";
import {OptionType} from "../../components/form/SearchableSelect";
import {UserViewSettings} from "../../models/common/ViewSettings";

/**
 * ViewSelectsState includes views and their select state.
 * the key is the viewIdentifier
 */
export interface ViewSelectsState<T extends OptionType = OptionType> {
    [ key: string ]: SelectStateByIdentifier<T>
}

export interface SelectState<T extends OptionType = OptionType> {
    selectSettings: UserViewSettings
    totalOptions: number
    allCachedOptions: any
    currentOptions: T[]
    request: RequestState
}

/**
 * SelectStateByIdentifier includes selects with their options and searchValue
 * key is the selectIdentifier
 */
export interface SelectStateByIdentifier<T extends OptionType = OptionType> {
    [key: string]: SelectState<T>
}

/**
 * all views with their select state
 */
export interface SelectComponentsState<T extends OptionType = OptionType> {
    selects: ViewSelectsState<T>
}

export const initialState: SelectComponentsState = {
    selects: {}
}

export const SelectComponentsReducer = ( state = initialState, action: FetchSelectOptionTypes ): SelectComponentsState => {
    const { selectIdentifier } = action;
    const viewIdentifier = action.userViewSettings?.viewIdentifier?.rawValue ?? "";
    const viewSelects = ( viewIdentifier in state.selects ) ? state.selects[ viewIdentifier ] : {};

    switch( action.type ) {
        case FETCH_INITIAL_SELECT_OPTIONS: {
            const selectSate = (selectIdentifier in viewSelects) ? viewSelects[selectIdentifier] : {
                searchValue: "",
                totalOptions: 0,
                allCachedOptions: [],
                currentOptions: []
            };

            return {
                ...state,
                selects: {
                    ...state.selects,
                    [viewIdentifier]: {
                        ...viewSelects,
                        [selectIdentifier]: {
                            ...selectSate,
                            selectSettings: action.userViewSettings,
                            request: {...getRequestState(true)}
                        }
                    }
                },
            };
        }
        case FETCH_SELECT_OPTIONS:
            const selectSate = ( selectIdentifier in viewSelects ) ? viewSelects[ selectIdentifier ] : { searchValue: "", totalOptions: 0, allCachedOptions: [], currentOptions: [] };

            return {
                ...state,
                selects: {
                    ...state.selects,
                    [ viewIdentifier ]: {
                        ...viewSelects,
                        [ selectIdentifier ]: {
                            ...selectSate,
                            selectSettings: action.userViewSettings,
                            request: { ...getRequestState(true) }
                        }
                    }
                },
            };
        case FETCH_SELECT_OPTIONS_SUCCESS:

            return {
                ...state,

                selects: {
                    ...state.selects,
                    [ viewIdentifier ]: {
                        ...viewSelects,
                        [ selectIdentifier ]: {
                            ...viewSelects[ selectIdentifier ],
                            allCachedOptions: mergeSelectOptions( viewSelects[selectIdentifier].allCachedOptions, action.payload.entries ),
                            totalOptions: action.payload.totalEntries,
                            currentOptions: action.payload.entries,
                            request: { ...getRequestStateSuccess() }
                        }
                    }
                },
            };
        case FETCH_SELECT_OPTIONS_ERROR:
            return {
                ...state,
                selects: {
                    ...state.selects,
                    [ viewIdentifier ]: {
                        ...viewSelects,
                        [ selectIdentifier ]: {
                            ...viewSelects[ selectIdentifier ],
                            request: { ...getRequestState( false, action.error ) }
                        }
                    }
                },

            }
        default:
            return state;
    }
};

export const mergeSelectOptions = <T extends OptionType = OptionType>( optionsOne: T[], optionsTwo: T[] ): T[] =>
    Array.from( new Set( optionsOne.concat( optionsTwo ) ));